import React, { useState, useEffect, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, startOfToday, endOfToday, startOfMonth, endOfMonth, subDays, startOfYear, endOfYear } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchExpenses,
  fetchExpenseCategories,
  createExpense,
  updateExpense,
  deleteExpense,
  approveExpense,
  fetchExpenseStatistics,
  fetchBranches
} from '@/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Eye,
  Edit,
  Trash2,
  Check,
  X,
  TrendingUp,
  TrendingDown,
  Clock,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import ExpenseDialog from '@/components/expenses/ExpenseDialog';
import ExpenseViewDialog from '@/components/expenses/ExpenseViewDialog';
import ExpenseApprovalDialog from '@/components/expenses/ExpenseApprovalDialog';
import ExpenseCategoriesTab from '@/components/expenses/ExpenseCategoriesTab';

const formatDateOnly = (value: string) => {
  if (!value) return 'N/A';
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return format(parsed, 'MMM dd, yyyy');
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return format(parsed, 'MMM dd, yyyy');
};

interface Expense {
  id: string;
  title: string;
  description?: string;
  amount: number;
  expense_date: string;
  expense_type: 'company' | 'personal' | 'reimbursement';
  payment_method: string;
  vendor_name?: string;
  receipt_number?: string;
  is_approved?: boolean | null;
  approved_by_name?: string;
  approved_at?: string;
  rejection_reason?: string;
  category_name: string;
  category_color: string;
  user_name: string;
  branch_id?: string | number;  // Add branch_id field
  branch_name?: string;
  notes?: string;
  created_at: string;
}

interface ExpenseCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
}

interface ExpenseStats {
  total_expenses: number;
  total_amount: number;
  approved_amount: number;
  pending_amount: number;
  rejected_amount: number;
  company_expenses: number;
  personal_expenses: number;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export default function ExpensesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const currencySymbol = useCurrencySymbol();

  const [allExpenses, setAllExpenses] = useState<Expense[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [branches, setBranches] = useState<{id: string; name: string}[]>([]);
  const [stats, setStats] = useState<ExpenseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Removed month-only filter; date range picker is sufficient

  // Dialogs
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');

  useEffect(() => {
    document.title = 'Expenses - Vixalor';
  }, []);

  // Load branches for admin users
  useEffect(() => {
    const loadBranches = async () => {
      if (isAdmin && branches.length === 0) {
        try {
          console.log('Loading branches for admin...');
          const branchesRes = await fetchBranches();
          console.log('Branches loaded:', branchesRes.data);
          setBranches(branchesRes.data);
        } catch (error) {
          console.error('Failed to load branches:', error);
          toast.error('Failed to load branches');
        }
      }
    };
    loadBranches();
  }, [isAdmin, branches.length]);

  const loadData = useCallback(async () => {
    try {
      // If branch filter is active, fetch all expenses to filter client-side
      // Otherwise use normal pagination
      const expenseParams = {
        page: branchFilter !== 'all' ? 1 : pagination.current_page,
        per_page: branchFilter !== 'all' ? 1000 : pagination.per_page, // Fetch all for client-side filtering
        search: searchTerm || undefined,
        category_id: categoryFilter !== 'all' ? categoryFilter : undefined,
        expense_type: typeFilter !== 'all' ? typeFilter : undefined,
        is_approved: statusFilter !== 'all' ? statusFilter : undefined,
        // Don't send branch_id since backend doesn't filter properly
        // branch_id: branchFilter !== 'all' ? branchFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      
      console.log('Fetching expenses with params:', expenseParams);
      
      const [expensesRes, statsRes] = await Promise.all([
        fetchExpenses(expenseParams),
        fetchExpenseStatistics({
          branch_id: branchFilter !== 'all' ? branchFilter : undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        })
      ]);
      
      console.log('API Response:', expensesRes.data);
      console.log('Branch filter sent to API:', branchFilter);
      
      const allExpensesData = expensesRes.data.data || [];
      setAllExpenses(allExpensesData);
      
      // Since backend is not filtering properly, do client-side filtering
      let filteredExpenses = allExpensesData;
      
      if (branchFilter !== 'all') {
        // Find the selected branch
        const selectedBranchIndex = branches.findIndex(b => b.id === branchFilter);
        const selectedBranch = branches.find(b => b.id === branchFilter);
        
        console.log('Selected branch:', selectedBranch);
        console.log('Selected branch index:', selectedBranchIndex);
        console.log('Available branches:', branches);
        
        filteredExpenses = allExpensesData.filter(expense => {
          const expenseBranchId = String(expense.branch_id || '');
          
          // Try multiple matching strategies due to ID mismatch issue
          // 1. Direct UUID match (in case it gets fixed)
          if (expenseBranchId === branchFilter) {
            return true;
          }
          
          // 2. Position-based match (1 = first branch, 2 = second branch, etc.)
          // This assumes expenses use numeric IDs based on branch order
          const numericBranchId = parseInt(expenseBranchId);
          if (!isNaN(numericBranchId) && numericBranchId > 0) {
            // Check if this numeric ID corresponds to the selected branch's position
            // Note: expense branch_id "1" might mean the first branch (index 0)
            const branchPosition = numericBranchId - 1; // Convert to 0-based index
            if (branchPosition === selectedBranchIndex) {
              return true;
            }
          }
          
          // 3. If main branch is selected, check for branch_id "1" (assuming 1 = main branch)
          if (selectedBranch?.is_main === 1 && expenseBranchId === "1") {
            return true;
          }
          
          return false;
        });
        console.log(`Filtered ${allExpensesData.length} expenses to ${filteredExpenses.length} for branch ${branchFilter}`);
        
        // For client-side filtering, implement pagination manually
        const startIndex = (pagination.current_page - 1) * pagination.per_page;
        const endIndex = startIndex + pagination.per_page;
        const paginatedExpenses = filteredExpenses.slice(startIndex, endIndex);
        
        setExpenses(paginatedExpenses);
        setPagination({
          current_page: pagination.current_page,
          per_page: pagination.per_page,
          total: filteredExpenses.length,
          last_page: Math.ceil(filteredExpenses.length / pagination.per_page)
        });
      } else {
        // No filter, use server pagination
        setExpenses(filteredExpenses);
        setPagination(expensesRes.data.pagination || { current_page: 1, per_page: 20, total: 0, last_page: 1 });
      }
      setStats(statsRes.data);

      // Branches are now loaded in a separate useEffect
    } catch (error) {
      toast.error('Failed to load expenses data');
    } finally {
      setLoading(false);
    }
  }, [pagination.current_page, pagination.per_page, searchTerm, categoryFilter, typeFilter, statusFilter, branchFilter, startDate, endDate]);

  // Load categories separately on mount to ensure they're always available
  useEffect(() => {
    const loadCategories = async () => {
      if (categories.length === 0) {
        try {
          const categoriesRes = await fetchExpenseCategories();
          console.log('Full categories response:', categoriesRes);
          console.log('Categories data:', categoriesRes.data);
          setCategories(categoriesRes.data);
          console.log('Categories loaded:', categoriesRes.data.length);
        } catch (error) {
          console.error('Failed to load categories:', error);
        }
      }
    };
    loadCategories();
  }, [categories.length]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = async () => {
    console.log('Categories available:', categories.length); // Debug log
    console.log('Categories before opening dialog:', categories);
    
    // Always ensure fresh categories are loaded
    try {
      const categoriesRes = await fetchExpenseCategories();
      console.log('Fresh categories loaded:', categoriesRes.data);
      setCategories(categoriesRes.data);
      
      // Wait a tick for state to update
      setTimeout(() => {
        setDialogMode('add');
        setSelectedExpense(null);
        setExpenseDialogOpen(true);
      }, 100);
    } catch (error) {
      toast.error('Failed to load categories');
      return;
    }
  };

  const handleEdit = async (expense: Expense) => {
    // Allow editing of all expenses (both approved and non-approved)
    // Only amount field will be disabled
    
    // Ensure categories are loaded before opening dialog
    if (categories.length === 0) {
      try {
        const categoriesRes = await fetchExpenseCategories();
        setCategories(categoriesRes.data);
      } catch (error) {
        toast.error('Failed to load categories');
        return;
      }
    }
    
    setDialogMode('edit');
    setSelectedExpense(expense);
    setExpenseDialogOpen(true);
  };

  const handleView = (expense: Expense) => {
    setSelectedExpense(expense);
    setViewDialogOpen(true);
  };

  const handleApprove = (expense: Expense) => {
    setSelectedExpense(expense);
    setApprovalDialogOpen(true);
  };

  const handleDelete = (expense: Expense) => {
    // Only admin can delete approved expenses
    if ((expense.is_approved === true || expense.is_approved === 1) && !isAdmin) {
      toast.error('Only admin can delete approved expenses');
      return;
    }
    setSelectedExpense(expense);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedExpense) return;

    try {
      await deleteExpense(selectedExpense.id);
      if (selectedExpense.is_approved) {
        toast.success('Expense deleted and transaction reversed successfully');
      } else {
        toast.success('Expense deleted successfully');
      }
      setDeleteDialogOpen(false);
      setSelectedExpense(null);
      loadData();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to delete expense');
    }
  };


  const handleExpenseSubmit = async (data: Omit<Expense, 'id'>) => {
    try {
      if (dialogMode === 'add') {
        await createExpense(data);
        toast.success('Expense created successfully');
      } else {
        await updateExpense(selectedExpense!.id, data);
        toast.success('Expense updated successfully');
      }
      setExpenseDialogOpen(false);
      setSelectedExpense(null);
      loadData();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Operation failed';
      toast.error(`Failed to ${dialogMode === 'add' ? 'create' : 'update'} expense: ${errorMessage}`);
    }
  };

  const handleApprovalSubmit = async (action: 'approved' | 'rejected', comments?: string) => {
    if (!selectedExpense) return;

    try {
      await approveExpense(selectedExpense.id, action, comments);
      toast.success(`Expense ${action} successfully`);
      setApprovalDialogOpen(false);
      setSelectedExpense(null);
      loadData();
    } catch (error) {
      toast.error(`Failed to ${action} expense`);
    }
  };

  const getStatusBadge = (expense: Expense) => {
    if (expense.is_approved === null) {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
    } else if (expense.is_approved === true || expense.is_approved === 1) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Approved</Badge>;
    } else {
      return <Badge variant="destructive" className="bg-red-100 text-red-800">Rejected</Badge>;
    }
  };

  const getSupplierNameForCategory = (expense: Expense): string => {
    if (expense.vendor_name && expense.vendor_name.trim()) {
      return expense.vendor_name.trim();
    }
    const description = expense.description || '';
    const match = description.match(/payment to (.+?) for invoice/i);
    return match?.[1]?.trim() || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg">Loading expenses...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Expense Management</h1>
          <p className="text-muted-foreground">Track and manage company and personal expenses</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="expenses" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="expenses" className="space-y-6">
          {/* Add Expense Button */}
          <div className="flex justify-end">
            <Button onClick={handleAdd}>
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </Button>
          </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{currencySymbol}{(Number(stats.total_amount) || 0).toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">{stats.total_expenses} expenses</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Approved</CardTitle>
              <Check className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{currencySymbol}{(Number(stats.approved_amount) || 0).toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Approved expenses</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{currencySymbol}{(Number(stats.pending_amount) || 0).toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Awaiting approval</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rejected</CardTitle>
              <X className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{currencySymbol}{(Number(stats.rejected_amount) || 0).toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Rejected expenses</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search expenses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    <div className="flex items-center">
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: category.color }}
                      />
                      {category.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="personal">Personal</SelectItem>
                <SelectItem value="reimbursement">Reimbursement</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>

            {isAdmin && (
              <Select value={branchFilter} onValueChange={(value) => {
                setBranchFilter(value);
                setPagination(prev => ({ ...prev, current_page: 1 }));
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Branch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Monthly selector removed */}

            {/* Date range picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange?.from ? (
                    dateRange.to ? (
                      `${format(dateRange.from, 'PP')} - ${format(dateRange.to, 'PP')}`
                    ) : (
                      `${format(dateRange.from, 'PP')} - ...`
                    )
                  ) : (
                    <span>Pick date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="end">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const fromDate = startOfToday();
                      const toDate = endOfToday();
                      setDateRange({ from: fromDate, to: toDate });
                      setStartDate(format(fromDate, 'yyyy-MM-dd'));
                      setEndDate(format(toDate, 'yyyy-MM-dd'));
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const toDate = endOfToday();
                      const fromDate = subDays(startOfToday(), 6);
                      setDateRange({ from: fromDate, to: toDate });
                      setStartDate(format(fromDate, 'yyyy-MM-dd'));
                      setEndDate(format(toDate, 'yyyy-MM-dd'));
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    Last 7 days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const toDate = endOfToday();
                      const fromDate = subDays(startOfToday(), 29);
                      setDateRange({ from: fromDate, to: toDate });
                      setStartDate(format(fromDate, 'yyyy-MM-dd'));
                      setEndDate(format(toDate, 'yyyy-MM-dd'));
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    Last 30 days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const fromDate = startOfMonth(new Date());
                      const toDate = endOfMonth(new Date());
                      setDateRange({ from: fromDate, to: toDate });
                      setStartDate(format(fromDate, 'yyyy-MM-dd'));
                      setEndDate(format(toDate, 'yyyy-MM-dd'));
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    This month
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const fromDate = startOfYear(new Date());
                      const toDate = endOfYear(new Date());
                      setDateRange({ from: fromDate, to: toDate });
                      setStartDate(format(fromDate, 'yyyy-MM-dd'));
                      setEndDate(format(toDate, 'yyyy-MM-dd'));
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    This year
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDateRange({});
                      setStartDate('');
                      setEndDate('');
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    Clear
                  </Button>
                </div>
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range || {});
                    const from = range?.from ? format(range.from, 'yyyy-MM-dd') : '';
                    const to = range?.to ? format(range.to, 'yyyy-MM-dd') : '';
                    setStartDate(from);
                    setEndDate(to);
                    setPagination(prev => ({ ...prev, current_page: 1 }));
                  }}
                  numberOfMonths={2}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setTypeFilter('all');
                setStatusFilter('all');
                setBranchFilter('all');
                setDateRange({});
                setStartDate('');
                setEndDate('');
                setPagination(prev => ({ ...prev, current_page: 1 }));
              }}
            >
              <Filter className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Expenses</CardTitle>
          <CardDescription>
            {(() => {
              if (!pagination.total) return 'Showing 0-0 of 0 expenses';
              const start = (pagination.current_page - 1) * pagination.per_page + (expenses.length ? 1 : 0);
              const end = (pagination.current_page - 1) * pagination.per_page + expenses.length;
              return `Showing ${start}-${end} of ${pagination.total} expenses`;
            })()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted By</TableHead>
                  {isAdmin && <TableHead>Branch</TableHead>}
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{expense.title}</div>
                        {expense.vendor_name && (
                          <div className="text-sm text-muted-foreground">{expense.vendor_name}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: expense.category_color }}
                        />
                        {expense.category_name}
                        {['to supplier', 'payment to supplier'].includes((expense.category_name || '').trim().toLowerCase()) && getSupplierNameForCategory(expense)
                          ? ` (${getSupplierNameForCategory(expense)})`
                          : ''}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">{currencySymbol}{(Number(expense.amount) || 0).toFixed(2)}</TableCell>
                    <TableCell>{formatDateOnly(expense.expense_date)}</TableCell>
                    <TableCell>{getStatusBadge(expense)}</TableCell>
                    <TableCell>{expense.user_name}</TableCell>
                    {isAdmin && <TableCell>{expense.branch_name || 'N/A'}</TableCell>}
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(expense)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(expense)}
                          title="Edit Expense"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {isAdmin && expense.is_approved === null && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApprove(expense)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        {expense.is_approved !== true && expense.is_approved !== 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(expense)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination + Per-page selector */}
          <div className="flex items-center justify-between space-x-2 py-4">
            <div className="text-sm text-muted-foreground">
              {(() => {
                if (!pagination.total) return 'Showing 0-0 of 0 expenses';
                const start = (pagination.current_page - 1) * pagination.per_page + (expenses.length ? 1 : 0);
                const end = (pagination.current_page - 1) * pagination.per_page + expenses.length;
                return `Showing ${start}-${end} of ${pagination.total} expenses`;
              })()}
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">Per page:</span>
                <Select
                  value={String(pagination.per_page)}
                  onValueChange={(val) =>
                    setPagination(prev => ({ ...prev, per_page: Number(val), current_page: 1 }))
                  }
                >
                  <SelectTrigger className="h-8 w-[90px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {pagination.last_page > 1 && (
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                    disabled={pagination.current_page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                    disabled={pagination.current_page >= pagination.last_page}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="categories">
          <ExpenseCategoriesTab />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ExpenseDialog
        open={expenseDialogOpen}
        onOpenChange={setExpenseDialogOpen}
        mode={dialogMode}
        expense={selectedExpense}
        categories={categories}
        branches={branches}
        isAdmin={isAdmin}
        userBranchId={user?.branch_id}
        onSubmit={handleExpenseSubmit}
      />

      <ExpenseViewDialog
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        expense={selectedExpense}
        onDelete={handleDelete}
        isAdmin={isAdmin}
      />

      {isAdmin && (
        <ExpenseApprovalDialog
          open={approvalDialogOpen}
          onOpenChange={setApprovalDialogOpen}
          expense={selectedExpense}
          onSubmit={handleApprovalSubmit}
        />
      )}


      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedExpense?.is_approved ? (
                <div className="space-y-2">
                  <p className="text-amber-600 font-semibold">
                    ⚠️ Warning: This is an approved expense!
                  </p>
                  <p>
                    Deleting "{selectedExpense?.title}" will reverse the transaction from your {selectedExpense?.payment_method === 'cash' ? 'cash' : 'bank'} account.
                  </p>
                  <p className="text-sm text-gray-600">
                    Amount to be credited back: ${(Number(selectedExpense?.amount) || 0).toFixed(2)}
                  </p>
                  <p className="font-semibold mt-2">This action cannot be undone.</p>
                </div>
              ) : (
                `Are you sure you want to delete "${selectedExpense?.title}"? This action cannot be undone.`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              {selectedExpense?.is_approved ? 'Delete & Reverse Transaction' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
